// Google Analytics 4. Off unless REACT_APP_GA_ID (a "G-XXXXXXXXXX" Measurement ID) is set at build
// time, so local development and builds without an ID never load Google's script.
//
// This is a single-page app, so GA's automatic page_view (which only fires on full page loads) is
// turned off and trackPageView() is called on every route change instead.

const GA_ID = process.env.REACT_APP_GA_ID?.trim();

// Admin screens aren't customer or partner traffic; keep them out of the reports.
const UNTRACKED_PREFIXES = ['/admin'];

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let started = false;

// Cookie consent (CookieConsent.tsx). Analytics cookies are set only after "Accept All Cookies";
// sign-in sessions use localStorage, which is essential and needs no consent.
export type CookieConsent = 'accepted' | 'denied';
const CONSENT_KEY = 'c2n_cookie_consent';
export const CONSENT_EVENT = 'c2n-cookie-consent';

export function getCookieConsent(): CookieConsent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'accepted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function setCookieConsent(choice: CookieConsent): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  if (choice === 'accepted') {
    if (GA_ID) (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = false;
    initAnalytics();
    trackPageView(window.location.pathname);
  } else {
    // Stop sending and remove Google Analytics cookies (_ga, _ga_<id>) for this site.
    if (GA_ID) (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = true;
    document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter((n) => n.startsWith('_ga')).forEach((name) => {
      const host = window.location.hostname;
      [host, `.${host}`, `.${host.replace(/^www\./, '')}`].forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      });
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function initAnalytics(): void {
  if (!GA_ID || started || typeof window === 'undefined' || getCookieConsent() !== 'accepted') return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  // gtag must push the `arguments` object itself, not an array copy.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(script);
}

// Path only (no query string): URLs like ?restoId= or ?q= shouldn't become separate "pages".
export function trackPageView(pathname: string): void {
  if (!window.gtag || UNTRACKED_PREFIXES.some((p) => pathname.startsWith(p))) return;
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}`,
    page_title: document.title
  });
}

// Named events for the actions that matter: searches, offer claims, partner applications, ...
export function trackEvent(name: string, params: Record<string, string | number | boolean | undefined> = {}): void {
  if (!window.gtag || UNTRACKED_PREFIXES.some((p) => window.location.pathname.startsWith(p))) return;
  window.gtag('event', name, params);
}
